import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { UserOnboarding } from '../services/onboarding'
import { useRequesterStore } from '../stores/requester'
export const useUserStore = defineStore('user', () => {
  const requesterX = useRequesterStore()
  const stored = localStorage.getItem('chullaAppUser')
  const apiUser = ref(stored ? JSON.parse(stored) : null)
  const firebaseUser = ref(null)
  const key = computed(() => apiUser.value?.key)
  const token = computed(() => apiUser.value?.token)
  const refreshToken = computed(() => apiUser.value?.refreshToken)
  const isLoggedIn = computed(() => apiUser.value != null)
  const workspaces = ref<Workspace[]>([])
  const persistedWorkspace = localStorage.getItem('chullaAppActiveWorkspace')
  const activeWorkspace = ref<string | null>(persistedWorkspace ?? null)
  const activeWorkspaceInfo = computed<Workspace | null>(() => {
    return workspaces.value.find((workspace) => workspace.id === activeWorkspace.value) ?? null
  })

  const billingEnabled = computed<boolean>(() => {
    return activeWorkspaceInfo.value?.billingEnabled ?? false
  })

  const isPersonalWorkspace = computed<boolean>(() => {
    return activeWorkspaceInfo.value?.type.toUpperCase() === 'PERSONAL'
  })

  const isBusinessWorkspace = computed<boolean>(() => {
    return activeWorkspaceInfo.value?.type.toUpperCase() === 'BUSINESS'
  })

  interface Workspace {
    id: string
    name: string
    billingEnabled: boolean
    type: string
    currency: string
    active: boolean
    isOwner: boolean
  }
  function setFirebaseUser(fbUser: any) {
    firebaseUser.value = fbUser
  }
  function setApiUser(user: any) {
    apiUser.value = user
    saveState()
  }
  function saveState() {
    const user = apiUser.value
    if (user) {
      localStorage.setItem('chullaAppUser', JSON.stringify(user))
    } else {
      localStorage.removeItem('chullaAppUser')
    }
  }
  function setTokens(tokens: any) {
    apiUser.value.key = tokens.key
    apiUser.value.token = tokens.token
    apiUser.value.refreshToken = tokens.refreshToken
    saveState()
  }
  function setActiveWorkspace(workspaceId: string | number) {
    const id = String(workspaceId)

    activeWorkspace.value = id

    localStorage.setItem('chullaAppActiveWorkspace', id)
  }
  async function me() {
    try {
      if (!isLoggedIn.value) return

      const profileResp = await requesterX.Get({
        route: '/auth/me',
        withAuth: true,
      })

      const userWorkspaces = profileResp.data?.user?.userWorkspaces ?? []

      workspaces.value = userWorkspaces.map((w: any) => ({
        id: String(w.workspaceInfo.id),
        name: String(w.workspaceInfo.name),
        billingEnabled: Boolean(w.workspaceInfo.billingEnabled),
        type: String(w.workspaceInfo.type),
        currency: String(w.workspaceInfo.currency),
        active: Boolean(w.workspaceInfo.active),
        isOwner: String(w.rolInfo?.code || '').toUpperCase() === 'OWNER',
      }))

      if (!workspaces.value.length) {
        activeWorkspace.value = null
        localStorage.removeItem('chullaAppActiveWorkspace')
        return
      }

      const activeWorkspaceExists = workspaces.value.some((workspace) => workspace.id === activeWorkspace.value)

      if (!activeWorkspaceExists) {
        setActiveWorkspace(workspaces.value[0].id)
      }
    } catch (error) {
      console.error(error)
    }
  }
  const onboarding = ref<UserOnboarding | null>(null)
  const onboardingLoaded = ref(false)

  function setOnboarding(value: UserOnboarding | null) {
    onboarding.value = value
    onboardingLoaded.value = true
  }

  function clearOnboarding() {
    onboarding.value = null
    onboardingLoaded.value = false
  }
  return {
    setFirebaseUser,
    firebaseUser,
    setApiUser,
    key,
    token,
    refreshToken,
    apiUser,
    setTokens,
    me,
    workspaces,
    activeWorkspace,
    activeWorkspaceInfo,
    billingEnabled,
    isPersonalWorkspace,
    isBusinessWorkspace,
    setActiveWorkspace,
    onboarding,
    onboardingLoaded,
    setOnboarding,
    clearOnboarding,
  }
})
